// CondensedPlayerStrip — the compact strip that replaces the full
// player frame when the user scrolls into Judgments / Up Next. Tap
// the strip to expand the player back to full size.
//
// Why this component is dumb (no scroll logic): the parent player
// owns the `condensed` boolean via `useCondensedPlayer` and just
// renders this strip when condensed is true. Keeping the visual + the
// behavior separate means the same strip works for movies, sermons,
// and audio (each player computes its own condensed boolean from its
// own scroll container).
//
// Props:
//   • title      — what's playing (always shown)
//   • subtitle   — optional (year, artist, speaker)
//   • poster     — optional thumbnail URL; renders a placeholder when
//                  missing (audio visualizer has no poster, e.g.)
//   • onExpand   — tap handler that flips condensed → false and
//                  scrolls back up. Wired from useCondensedPlayer.expand.
//   • accent     — Tailwind color name (forest | terracotta | …)
//                  for the expand chevron tint. Defaults to cream.
//
// First-time coach mark:
// The first time a user EVER sees this strip we fade in a pulsing
// "Tap to expand" hint above the strip. We persist dismissal in
// localStorage so it never reappears for the same browser. The hint
// also auto-fades after 4 seconds even if the user does nothing,
// since by then they've seen it and pressing the strip itself is
// the natural next action.
import React, { useEffect, useState } from "react";
import { CaretUp, MusicNote, ArrowLineUp } from "@phosphor-icons/react";

const COACH_KEY = "hw-condensed-player-coach-seen";

export default function CondensedPlayerStrip({
  title,
  subtitle = "",
  poster = null,
  onExpand,
  accent = "cream",
  testid = "condensed-player-strip",
}) {
  // Show coach the first time the strip appears, then NEVER again
  // for the same browser. We default to `false` and flip to `true`
  // in an effect so SSR / initial render doesn't fight with
  // localStorage hydration.
  const [showCoach, setShowCoach] = useState(false);
  useEffect(() => {
    try {
      if (window.localStorage.getItem(COACH_KEY) === "1") return;
      setShowCoach(true);
      // Auto-fade after 4 s. Also marks as seen so we don't show
      // it again on the next condensed-mode entry within the same
      // session — once is enough.
      const t = setTimeout(() => {
        setShowCoach(false);
        try { window.localStorage.setItem(COACH_KEY, "1"); } catch (_) {}
      }, 4000);
      return () => clearTimeout(t);
    } catch (_) { /* ignore — private mode etc. */ }
    return undefined;
  }, []);

  const dismissCoach = () => {
    setShowCoach(false);
    try { window.localStorage.setItem(COACH_KEY, "1"); } catch (_) {}
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { dismissCoach(); onExpand && onExpand(e); }}
        data-testid={testid}
        aria-label={`Expand player — ${title}`}
        title="Tap to expand player"
        className="absolute inset-0 z-30 w-full flex items-center gap-3 px-3 sm:px-4 hw-player-deep backdrop-blur-md text-cream text-left hover:opacity-90 transition-opacity"
      >
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-cream/10 grid place-items-center shrink-0">
          {poster ? (
            <img src={poster} alt="" className="w-full h-full object-cover" />
          ) : (
            <MusicNote size={20} weight="fill" className="opacity-70" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.18em] uppercase text-cream/60 font-semibold">
            Now playing
          </div>
          <div className="font-heading text-sm sm:text-base truncate">
            {title || "Untitled"}
          </div>
          {subtitle && (
            <div className="text-[11px] text-cream/65 truncate">{subtitle}</div>
          )}
        </div>
        <div className={`shrink-0 flex flex-col items-center text-${accent} text-[9px] tracking-[0.18em] uppercase font-bold`}>
          <CaretUp size={16} weight="bold" />
          <span>Expand</span>
        </div>
      </button>
      {showCoach && (
        <div
          data-testid={`${testid}-coach`}
          aria-hidden="true"
          className="absolute -top-12 left-1/2 -translate-x-1/2 z-40 pointer-events-none animate-fade-up"
        >
          <div className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-cream text-forest text-[10px] tracking-[0.18em] uppercase font-bold shadow-2xl ring-1 ring-forest/20 animate-pulse">
            <ArrowLineUp size={12} weight="bold" />
            Tap to expand
          </div>
        </div>
      )}
    </>
  );
}
